import TwoFactorChallenge from './components/TwoFactorChallenge';
import React, { Component, Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { App as AntApp, ConfigProvider, Layout, Spin } from 'antd';
import Navbar from './components/Navbar';
import FloatingHelp from './components/FloatingHelp';
import MetaPageViewTracker from './components/MetaPageViewTracker';
import TikTokPageViewTracker from './components/TikTokPageViewTracker';
import SiteConfigProvider from './context/SiteConfigContext';
import axiosInstance from './api/axiosConfig';
import { I18nProvider } from './i18n/I18nProvider';
import {
  authenticateTelegramMiniApp,
  clearTelegramMiniAppLaunchParams,
  getTelegramMiniApp,
  getTelegramMiniAppInitData,
  isTelegramMiniAppSession,
  prepareTelegramMiniApp
} from './utils/telegramMiniApp';
import { safeGetItem, safeJsonSet, safeSetItem } from './utils/safeStorage';
import useCurrencySync from './hooks/useCurrencySync';
import usePageBack from './hooks/usePageBack';
import { storefrontThemeTokens, buttonThemeTokens, inputThemeTokens } from './theme/brand';
import './App.css'; 

const lazyWithPreload = (loader) => {
  const Component = lazy(loader);
  Component.preload = loader;
  return Component;
};

const lazyGoogleAuthPage = loader => lazyWithPreload(async () => {
  // Fetch the route and provider together, but mount Google's SDK only on auth pages.
  const [page, provider] = await Promise.all([loader(), import('./components/GoogleAuthProvider')]);
  const Page = page.default;
  const Provider = provider.default;
  return { default: function GoogleAuthPage(props) { return <Provider><Page {...props} /></Provider>; } };
});

const Home = lazyWithPreload(() => import(/* webpackChunkName: "page-home" */ './pages/Home'));
const GameDetails = lazyWithPreload(() => import(/* webpackChunkName: "page-game-details" */ './pages/GameDetails'));
const NotFound = lazyWithPreload(() => import(/* webpackChunkName: "page-not-found" */ './pages/NotFound'));
const Login = lazyGoogleAuthPage(() => import(/* webpackChunkName: "page-login" */ './pages/Login'));
const Register = lazyGoogleAuthPage(() => import(/* webpackChunkName: "page-register" */ './pages/Register'));
const ForgotPassword = lazyWithPreload(() => import(/* webpackChunkName: "page-forgot-password" */ './pages/ForgotPassword'));
const Profile = lazyWithPreload(() => import(/* webpackChunkName: "page-profile" */ './pages/Profile'));
const MyOrders = lazyWithPreload(() => import(/* webpackChunkName: "page-orders" */ './pages/MyOrders'));
const MyWallet = lazyWithPreload(() => import(/* webpackChunkName: "page-wallet" */ './pages/MyWallet'));
const Invoice = lazyWithPreload(() => import(/* webpackChunkName: "page-invoice" */ './pages/Invoice'));
const SpinWheel = lazyWithPreload(() => import(/* webpackChunkName: "page-spin-wheel" */ './pages/SpinWheel'));
const Legal = lazyWithPreload(() => import(/* webpackChunkName: "page-legal" */ './pages/Legal'));
const Help = lazyWithPreload(() => import(/* webpackChunkName: "page-help" */ './pages/Help'));
const CouponPage = lazyWithPreload(() => import(/* webpackChunkName: "page-coupons" */ './pages/Coupon'));

const preloadedRouteChunks = new Set();
const preloadRouteChunk = (name, Component) => {
  if (!Component?.preload || preloadedRouteChunks.has(name)) {
    return;
  }

  preloadedRouteChunks.add(name);
  Component.preload().catch(() => {
    preloadedRouteChunks.delete(name);
  });
};

const routeToPreload = pathname => {
  if (pathname.startsWith('/game/')) return ['game-details', GameDetails];
  if (pathname.startsWith('/invoice')) return ['invoice', Invoice];
  return { '/': ['home', Home], '/login': ['login', Login], '/register': ['register', Register], '/help': ['help', Help], '/wallet': ['wallet', MyWallet], '/orders': ['orders', MyOrders], '/profile': ['profile', Profile] }[pathname];
};

// 🆕 GLOBAL REFERRAL TRACKER
// This automatically catches /re/xxxxx on ANY page, saves it, and cleans the URL
const ReferralTracker = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if the URL contains "/re/CODE"
    const match = location.pathname.match(/\/re\/([a-zA-Z0-9_]+)/);
    
    if (match) {
      const referralCode = match[1];
      
      // 1. Save the referral code to Local Storage so it stays even if they click "Home"
      safeSetItem('referralCode', referralCode);
      console.log('Referral Code Saved:', referralCode); // Optional: for debugging
      
      // 2. Remove the /re/... part from the URL to keep it clean and prevent routing errors
      const cleanPath = location.pathname.replace(/\/re\/[a-zA-Z0-9_]+/, '') || '/';
      
      // 3. Redirect to the clean path seamlessly
      navigate(cleanPath + location.search, { replace: true });
    }
  }, [location, navigate]);

  return children;
};

const RouteFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center">
    <Spin size="large" />
  </div>
);

const ScrollToTop = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    const root = document.getElementById('root');
    if (root) {
      root.scrollTop = 0;
    }
  }, [location.pathname, location.search]);

  return null;
};

const STOREFRONT_VISITOR_ID_KEY = 'jlastore.storefront.visitor-id.v1';

const getOrCreateStorefrontVisitorId = () => {
  const existingId = safeGetItem(STOREFRONT_VISITOR_ID_KEY);
  if (/^[a-zA-Z0-9_-]{16,64}$/.test(existingId || '')) return existingId;

  const visitorId = window.crypto?.randomUUID?.()
    || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  safeSetItem(STOREFRONT_VISITOR_ID_KEY, visitorId);
  return visitorId;
};

const StorefrontPresenceHeartbeat = () => {
  const location = useLocation();

  useEffect(() => {
    let stopped = false;
    let inFlight = false;
    const visitorId = getOrCreateStorefrontVisitorId();

    const sendHeartbeat = async () => {
      if (
        stopped
        || inFlight
        || document.visibilityState !== 'visible'
        || !navigator.onLine
      ) {
        return;
      }

      inFlight = true;
      try {
        await axiosInstance.post('/presence/heartbeat', {
          source: 'storefront',
          visitor_id: visitorId,
          path: location.pathname
        }, {
          skipCacheInvalidation: true
        });
      } catch (error) {
        if (error.response?.status !== 401) {
          console.warn('[presence] Heartbeat failed:', error.message);
        }
      } finally {
        inFlight = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') sendHeartbeat();
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 20 * 1000);
    window.addEventListener('focus', sendHeartbeat);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', sendHeartbeat);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname]);

  return null;
};

const CHUNK_RELOAD_KEY = 'jlastore_chunk_reload_at';

const isChunkLoadError = (error) => {
  const message = String(error?.message || error || '');

  return /chunkloaderror|loading chunk \d+ failed|failed to fetch dynamically imported module|importing a module script failed/i.test(message);
};

class ChunkLoadRecoveryBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error) || typeof window === 'undefined') {
      console.error('[storefront] Route render failed', error);
      return;
    }

    const now = Date.now();
    const lastReloadAt = Number(safeGetItem(CHUNK_RELOAD_KEY, '0', { type: 'session' }) || 0);

    if (now - lastReloadAt > 10000) {
      safeSetItem(CHUNK_RELOAD_KEY, String(now), { type: 'session' });
      window.location.reload();
      return;
    }

    console.error('[storefront] Chunk reload already attempted', error);
  }

  render() {
    if (this.state.hasError) {
      return <RouteFallback />;
    }

    return this.props.children;
  }
}

const RouteSuspenseFallback = () => {
  const location = useLocation();

  if (location.pathname === '/') {
    return null;
  }

  return <RouteFallback />;
};

const RoutePreloader = () => {
  useEffect(() => {
    const preloadIntent = event => {
      const link = event.target?.closest?.('a[href]');
      if (!link || link.origin !== window.location.origin) return;
      const target = routeToPreload(link.pathname);
      if (target) preloadRouteChunk(...target);
    };
    document.addEventListener('pointerover', preloadIntent, { passive: true });
    document.addEventListener('focusin', preloadIntent);
    return () => { document.removeEventListener('pointerover', preloadIntent); document.removeEventListener('focusin', preloadIntent); };
  }, []);
  return null;
};

const isTelegramBackButtonSupported = (webApp) => {
  if (!webApp?.BackButton) {
    return false;
  }

  if (typeof webApp.isVersionAtLeast === 'function') {
    return webApp.isVersionAtLeast('6.1');
  }

  return true;
};

const TelegramHeaderBridge = () => {
  const location = useLocation();
  const goBack = usePageBack(location.pathname === '/profile' && location.hash === '#referral' ? '/profile' : '/');

  useEffect(() => {
    if (!isTelegramMiniAppSession()) {
      return undefined;
    }

    const webApp = getTelegramMiniApp();
    if (!isTelegramBackButtonSupported(webApp)) {
      return undefined;
    }

    const backButton = webApp?.BackButton;

    const isRootRoute = location.pathname === '/';

    if (isRootRoute) {
      backButton.hide();
      return undefined;
    }

    backButton.show();
    backButton.onClick(goBack);

    return () => {
      backButton.offClick(goBack);
      backButton.hide();
    };
  }, [location.pathname, goBack]);

  return null;
};

const StoreLayout = () => {
  const { pathname } = useLocation();
  const isInvoiceRoute = pathname === '/invoice' || pathname.startsWith('/invoice/') || pathname === '/payment/heleket/result';
  return (
  <Layout
    className={`store-page-shell${['/profile', '/orders', '/wallet', '/coupons', '/help'].includes(pathname) ? ' store-profile-layout' : ''}${isInvoiceRoute ? ' store-invoice-layout' : ''}`}
    style={{
      minHeight: '100vh',
      background: 'var(--color-background)',
      paddingTop: 'var(--store-header-height)'
    }}
  >
    <Navbar />
    <Outlet />
    <FloatingHelp />
  </Layout>
  );
};

function App() {
  const [isTelegramBootstrapLoading, setIsTelegramBootstrapLoading] = useState(
    () => Boolean(getTelegramMiniAppInitData()) && !safeGetItem('accessToken')
  );

  useEffect(() => {
    let isCancelled = false;

    const initData = getTelegramMiniAppInitData();
    const existingToken = safeGetItem('accessToken');

    if (initData) {
      prepareTelegramMiniApp();
    }

    if (!initData) {
      setIsTelegramBootstrapLoading(false);
      return undefined;
    }

    if (existingToken) {
      clearTelegramMiniAppLaunchParams();
      setIsTelegramBootstrapLoading(false);
      return undefined;
    }

    const bootstrapTelegramSession = async () => {
      try {
        const response = await authenticateTelegramMiniApp(axiosInstance);

        if (isCancelled || response?.data?.status !== 'success') {
          return;
        }

        safeSetItem('accessToken', response.data.data.accessToken);
        safeJsonSet('user', response.data.data);
        clearTelegramMiniAppLaunchParams();
      } catch (error) {
        console.error('[telegram-miniapp] Auto sign-in failed', error);
      } finally {
        if (!isCancelled) {
          setIsTelegramBootstrapLoading(false);
        }
      }
    };

    bootstrapTelegramSession();

    return () => {
      isCancelled = true;
    };
  }, []);

  useCurrencySync();

  if (isTelegramBootstrapLoading) {
    return (
      <Layout className="store-page-shell" style={{ minHeight: '100vh', background: 'var(--color-background)' }}>
        <TwoFactorChallenge />
        <RouteFallback />
      </Layout>
    );
  }

  return (
    <ConfigProvider
      componentSize="middle"
      theme={{
        token: {
          ...storefrontThemeTokens,
          colorSuccess: '#159a74',
          colorSuccessBg: '#eaf8f3',
          colorSuccessBorder: '#b9e7d8',
          colorWarning: '#b7791f',
          colorError: '#c83c4d',
        },
        components: {
          Button: buttonThemeTokens,
          Input: inputThemeTokens,
          InputNumber: inputThemeTokens,
          Select: { controlHeight: 40, controlHeightLG: 40, controlHeightSM: 40, fontSize: 14, fontSizeLG: 14 },
          DatePicker: { controlHeight: 40, controlHeightLG: 40, controlHeightSM: 40, fontSize: 14, fontSizeLG: 14 },
          Card: { boxShadow: 'none' },
          Modal: { boxShadow: 'none' },
          Table: { headerBg: '#f7f8fa', headerColor: '#374151', rowHoverBg: '#fafafa' }
        }
      }}
    >
      <AntApp>
      <TwoFactorChallenge />
      <I18nProvider>
      <SiteConfigProvider>
        <BrowserRouter>
        {/* Wrap everything in the ReferralTracker so it has access to useLocation/useNavigate */}
        <ReferralTracker>
          <MetaPageViewTracker />
          <TikTokPageViewTracker />
          <ScrollToTop />
          <StorefrontPresenceHeartbeat />
          <TelegramHeaderBridge />
          <RoutePreloader />
          <ChunkLoadRecoveryBoundary>
            <Suspense fallback={<RouteSuspenseFallback />}>
              <Routes>
                <Route element={<StoreLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/orders" element={<MyOrders />} />
                  <Route path="/wallet" element={<MyWallet />} />
                  <Route path="/coupons" element={<CouponPage />} />
                  <Route path="/game/:id" element={<GameDetails />} />
                  <Route path="/invoice" element={<Invoice />} />
                  <Route path="/invoice/:tranId" element={<Invoice />} />
                  <Route path="/spin/:tranId" element={<SpinWheel />} />
                  <Route path="/lucky-box/:tranId" element={<SpinWheel />} />
                  <Route path="/payment/heleket/result" element={<Invoice />} />
                  <Route path="/policies" element={<Legal />} />
                  <Route path="/terms" element={<Legal />} />
                  <Route path="/privacy" element={<Legal />} />
                  <Route path="/refund" element={<Legal />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Suspense>
          </ChunkLoadRecoveryBoundary>
        </ReferralTracker>
        </BrowserRouter>
      </SiteConfigProvider>
      </I18nProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
